import React, { useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Avatar, IconButton, Tooltip, Box, Chip, Switch, Stack, Typography } from "@mui/material";
import { formatDateTimePtBR } from "../utils/dateTime";
import { useTranslation } from "react-i18next";

const UserTable = ({
  users = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditUser,
  onDeleteUser,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
}) => {
  const { t } = useTranslation("settings");
  const rows = useMemo(
    () =>
      (users || []).map((u) => ({
        id: u?.id,
        nome: u?.nome || "",
        email: u?.email || "",
        cargo: u?.cargo_nome || "",
        ura: Number(u?.ura) === 1,
        statusRaw: Number(u?.status) === 1 ? 1 : 0,
        online: !!u?.is_online,
        idInterno: u?.id_interno || "",
        createdAt: u?.data_cadastro || null,
        updatedAt: u?.data_ultima_alteracao || null,
        lastAccess: u?.data_ultimo_acesso || null,
        avatarUrl: u?.imagem || "",
      })),
    [users]
  );

  const initials = (s) =>
    String(s || "—")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "—";
      
  const columns = useMemo(() => [
    {
      field: "nome",
      headerName: t("userTable.colUser"),
      width: 380,
      renderCell: (p) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, width: "100%" }}   // permite ellipsis no texto
        >
          {/* Coluna esquerda: avatar + presença */}
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Avatar
              src={p?.row?.avatarUrl || undefined}
              sx={{ width: 24, height: 24, fontSize: 11, bgcolor: "grey.200", color: "text.primary" }}
            >
              {!p?.row?.avatarUrl && initials(p?.row?.nome)}
            </Avatar>
            <Box
              sx={{
                position: "absolute",
                right: -1,
                bottom: -1,
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: p?.row?.online ? "success.main" : "text.disabled",
                border: "2px solid",
                borderColor: "background.paper",
              }}
            />
          </Box>

          {/* Coluna direita: nome em cima, e-mail embaixo */}
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Typography
              fontWeight={600}
              noWrap
              title={p?.row?.nome ?? "—"}
            >
              {p?.row?.nome ?? "—"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={p?.row?.email ?? "—"}
              sx={{ display: "block" }}
            >
              {p?.row?.email ?? "—"}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    // {
    //   field: "nome",
    //   headerName: "Usuário",
    //   flex: 1,
    //   minWidth: 340,
    //   renderCell: (p) => (
    //     <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
    //       {/* Avatar + presença (sem forçar altura) */}
    //       <Box sx={{ position: "relative", flexShrink: 0 }}>
    //         <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: "grey.200", color: "text.primary" }}>
    //           {initials(p?.row?.nome)}
    //         </Avatar>
    //         <Box
    //           sx={{
    //             position: "absolute", right: -1, bottom: -1,
    //             width: 8, height: 8, borderRadius: "50%",
    //             bgcolor: p?.row?.online ? "success.main" : "text.disabled",
    //             border: "2px solid", borderColor: "background.paper",
    //           }}
    //         />
    //       </Box>

    //       {/* Texto em uma única linha com ellipsis */}
    //       <Typography
    //         noWrap
    //         title={`${p?.row?.nome ?? "—"} • ${p?.row?.email ?? "—"}`}
    //         sx={{ minWidth: 0 }}
    //       >
    //         <Box component="span" sx={{ fontWeight: 600 }}>
    //           {p?.row?.nome ?? "—"}
    //         </Box>
    //         {p?.row?.email && (
    //           <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
    //             • {p.row.email}
    //           </Box>
    //         )}
    //       </Typography>
    //     </Stack>
    //   ),
    // },
    { field: "cargo", headerName: t("userTable.colRole"), width: 180 },
    {
      field: "ura",
      headerName: t("userTable.colType"),
      width: 100,
      renderCell: (p) =>
        p?.row?.ura ? (
          <Chip size="small" label="ChatBot" color="warning" variant="outlined" />
        ) : (
          <Typography variant="caption" color="text.secondary">{t("userTable.human")}</Typography>
        ),
    },
    // {
    //   field: "updatedAt",
    //   headerName: "Última alteração",
    //   width: 180,
    //   // usar renderCell evita acessos a params.value quando params é null
    //   renderCell: (p) => <span>{fmtDateTime(p?.row?.updatedAt)}</span>,
    // },
    {
      field: "lastAccess",
      headerName: t("userTable.colLastAccess"),
      width: 180,
      renderCell: (p) => <span>{formatDateTimePtBR(p?.row?.lastAccess)}</span>,
    },
    {
      field: "status",
      headerName: t("userTable.colStatus"),
      width: 160,
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            size="small"
            checked={Number(p?.row?.statusRaw) === 1}
            onChange={() => onToggleStatus?.(p?.row)}
          />
          <Chip
            size="small"
            label={Number(p?.row?.statusRaw) === 1 ? t("shared.active") : t("shared.inactive")}
            color={Number(p?.row?.statusRaw) === 1 ? "success" : "default"}
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: t("userTable.colActions"),
      width: 140,
      renderCell: (p) => (
        <Box display="flex" gap={1}>
          <Tooltip title={t("shared.edit")}>
            <IconButton size="small" onClick={() => onEditUser?.(p?.row)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("shared.delete")}>
            <IconButton size="small" onClick={() => onDeleteUser?.(p?.row?.id)}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [t, onEditUser, onDeleteUser, onToggleStatus]);

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle={t("userTable.exportTitle")}
      exportFileName="usuarios"
      onDeleteSelected={onDeleteSelected}
      searchPlaceholder={t("userTable.searchPlaceholder")}
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default UserTable;
